
import Axios from 'axios'
import { Isomorphic } from '@/helpers/'
import Url from 'url'
const { URLSearchParams } = Isomorphic

import {
  SHOP_ORIGIN,
  SEARCH_API,
} from './Configuration'
import { sortFacetByMatch } from '@/helpers/categoryFilters'
import {
  BreadcrumbsType,
  CategoryESresponse,
  CategoryPageApiResponse,
  FacetsType,
  SearchPageApiResponse,
} from '@/types/ElasticSearch/SearchAPIResponse'
import { Product } from '@/types'
import { log } from './Log'
import { PLP_Items_Per_Page } from '@/data/constants'

// display only if options are available rule is boolean or there is a selected option.
const isFacetAvailable: (f: FacetsType) => boolean = (f) => (
  !!f.available.length ||
  (
    `${f.type}`.toUpperCase() === 'BOOL'

  ) ||
  !!f.available.some(({ selected }) => selected)
)

const FacetWhitelist = {
  // facet is `Category`
  isCategory: ({ name }) => name === 'Category',
  // facet is not a hierarchical filter
  isMultiSelect: ({ rule }) => rule !== 'HIER',
  // display only if all products in list are not in the option's bucket or if it is a selected option
  isNotFullCoverage: ({ available }, hits) => available.some(({ count, selected }) => (count && count !== hits.length) || selected),
  // display height and width ranges for furniture, arts, mirror
  onlyIncludeHeightWidthForFurnitureArtsMirror: (facet, breadcrumb) => {
    try {
      const furnitureArtsMirror = new Set([
        40315, // art & mirrors
        40284, // furniture
      ])
      const heightWidth = new Set([
        'heightRange',
        'widthRange'
      ])
      const shouldExclude = (
        !furnitureArtsMirror.has(parseInt(breadcrumb.id)) &&
        heightWidth.has(facet.machineName)
      )
      return shouldExclude
    } catch {
      return false
    }
  }
}

const matchQuery: (p: string) => RegExpMatchArray | null = (path: string = '') => path.match(/\?.*$/) || null
const unpack: <T>(P: { data: T }) => T = response => response.data
const searchHeaders = () => (process.browser ? {} : {
  referer: SHOP_ORIGIN,
  origin: SHOP_ORIGIN,
  'Content-Type': 'application/json'
})

const byLegacyId: <T>(p: { params: { [key: string]: string }, legacyId: string, path: string }) => Promise<T> = async ({ params, legacyId, path }) => {
  let resource = ''
  if (path.startsWith('/b')) {
    resource = 'brand'
  } else {
    resource = 'category'
  }
  return unpack(await Axios.get(`${SEARCH_API}/${resource}/${legacyId}`, { params, headers: searchHeaders() }))
}
export type searchByCategoryFunctionType = (p: { path: string, limit?: number, page?: number, legacyId: string }) => Promise<null | { category: CategoryESresponse, facets: FacetsType[], products: Product[], breadcrumbs: BreadcrumbsType[], total: number }>


const searchByCategory: searchByCategoryFunctionType = async ({ path, limit = PLP_Items_Per_Page, page = 0, legacyId }) => {
  log(`SEARCH Category ${JSON.stringify({ path, limit, page, legacyId })}`)
  const query = matchQuery(path)
  const params = new URLSearchParams(query ? query[0] : '?')
  params.delete('legacyId')
  params.set('page', `${page || 1}`)
  params.set('limit', `${limit || 48}`)
  const response = await byLegacyId<CategoryPageApiResponse>({ params: paramsToObject(params.entries()), legacyId, path })
  if (!response) {
    return null
  }
  const products = response.hits
  const total = response.total
  const multiSelectFacets = response.facets.multiSelect
  const breadcrumbs = response.breadcrumbs
  const addMachineName: (k: string) => FacetsType =
    (key: string) => ({ ...multiSelectFacets[key], machineName: key })

  const isRelevantFacet: (f: FacetsType) => boolean =
    (f) => (
      isFacetAvailable(f) &&
      FacetWhitelist.isNotFullCoverage(f, products) &&
      !FacetWhitelist.onlyIncludeHeightWidthForFurnitureArtsMirror(f, breadcrumbs[0])
    )

  const facets = (
    Object.keys(multiSelectFacets)
      .map(addMachineName)
      .filter(isRelevantFacet)
      .map(facet => sortFacetByMatch(facet))
  )
  const category = response.category
  return { category, products, facets, breadcrumbs, total }
}
const searchAddParams: (p: { url: string, limit?: number, page?: number }) => string = ({ url, limit, page }) => {
  const urlObject = Url.parse(url)
  const params = new URLSearchParams(urlObject.query || '')
  params.set('limit', `${limit || 48}`)
  params.set('page', `${(page || 0) + 1}`)
  urlObject.query = params.toString()
  return Url.format(urlObject)
}

const bySearchQuery: <T>(p: { params: { [key: string]: string } }) => Promise<T> = async ({ params }) => (
  unpack(await Axios.get(`${SEARCH_API}/search/`, { params, headers: searchHeaders() }))
)

export type searchByQueryResponse = {
  products: Product[],
  facets: FacetsType[],
  total: number
}
const searchByQuery: (p: { limit?: number, page?: number, path: string }) => Promise<null | searchByQueryResponse> = async ({ limit = PLP_Items_Per_Page, page, path }) => {
  const query = Search.matchQuery(path)

  const params = new URLSearchParams(query ? query[0] : '?')
  params.set('page', `${page || 1}`)
  params.set('limit', `${limit || 48}`)
  try {
    const response = await bySearchQuery<SearchPageApiResponse>({ params: paramsToObject(params.entries()) })
    // map fields to resemble the categories endpoint so that categories components can be used more seamlessly
    const products = response.hits
    const total = response.total
    const multiSelectFacets = response.facets.multiSelect
    const facets = Object.keys(multiSelectFacets)
      .map(machineName => ({ ...multiSelectFacets[machineName], machineName }))
      .filter(facet => (isFacetAvailable(facet) && FacetWhitelist.isNotFullCoverage(facet, products)))
    return { products, facets, total }
  } catch (e) {
    log(e)
    return null
  }
}
const Search = {
  // This one is only client-side, but Parky uses a weird thing where Referer is needed on SERVER-SIDE.
  // Be aware of this if you ever attempt to use this on the server in the future. 2020-01-20
  suggest: async query => (await Axios.get(`${SEARCH_API}/suggest`, { params: { query }, headers: Search.headers() })).data,
  headers: searchHeaders,
  // Adding one because it's not zero-indexed for pages, apparently. Makes it so we have 0-indexed elsewhere in code.
  addParams: searchAddParams,
  unpack: response => response.data,
  byLegacyId,
  matchQuery,
  category: searchByCategory,
  search_query: searchByQuery
}

export default Search


function paramsToObject(entries) {
  let result = {}
  for (let entry of entries) { // each 'entry' is a [key, value] tupple
    const [key, value] = entry;
    result[key] = value;
  }
  return result;
}
